/**
 * Version: V0.0.3.0
 * Created: 2026-06-13
 * Updated: 2026-06-13
 * Description: Public template layout that holds Navbar, Footer, and global theme sync logic.
 */

import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { MessageSquare } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import CookieConsentBanner from '../components/layout/CookieConsentBanner';

const LiveChatWidget = lazy(() => import('../components/common/LiveChatWidget'));

export default function MainLayout({ children, session, onLogout, theme, setTheme }) {
  const { language } = useLanguage();
  const location = useLocation();
  const isRtl = language === 'ar';
  const [chatOpen, setChatOpen] = useState(false);
  
  // Hide global navigation elements on dashboard routes, login, signup, activate, forgot-password, reset-password, and tickets routes
  const isDashboard = location.pathname.startsWith('/dashboard') || 
                      location.pathname.startsWith('/tickets') || 
                      location.pathname === '/login' || 
                      location.pathname === '/signup' || 
                      location.pathname === '/activate' ||
                      location.pathname === '/forgot-password' ||
                      location.pathname === '/reset-password';

  // Apply theme dynamically to the document body
  useEffect(() => {
    const applyTheme = () => {
      let activeTheme = theme;
      if (theme === 'system') {
        activeTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      document.body.setAttribute('data-theme', activeTheme);
      document.body.style.backgroundColor = 'var(--color-background)';
      document.body.style.color = 'var(--color-text)';
      document.body.style.transition = 'background-color 0.3s ease, color 0.3s ease';
    };

    applyTheme();
    localStorage.setItem('dentiefy-theme', theme);

    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const listener = () => applyTheme();
      mediaQuery.addEventListener('change', listener);
      return () => mediaQuery.removeEventListener('change', listener);
    }
  }, [theme]);

  const [devBarOffset, setDevBarOffset] = React.useState(0);

  React.useEffect(() => {
    const devDomain = process.env.REACT_APP_MAIN_DOMAIN_DEV || 'dentiefy.test';
    const isDev = process.env.NODE_ENV === 'development' || 
                  window.location.hostname.includes('localhost') || 
                  window.location.hostname.includes('127.0.0.1') || 
                  window.location.hostname.includes(devDomain);

    if (!isDev) return;

    const calcOffset = () => {
      // Development role state controls only presentation. Authentication is
      // always resolved from the server session in App.
      const isSim = localStorage.getItem('dentiefy-dev-role') !== 'VISITOR';

      if (!isSim) {
        setDevBarOffset(0);
        return;
      }

      const collapsed = localStorage.getItem('dentiefy-dev-bar-collapsed') === 'true';
      setDevBarOffset(collapsed ? 0 : 54);
    };

    calcOffset();
    window.addEventListener('dev-bar-resize', calcOffset);
    return () => window.removeEventListener('dev-bar-resize', calcOffset);
  }, [session]);

  useEffect(() => {
    if (chatOpen && !isDashboard) {
      document.body.classList.add('chat-sidebar-open');
    } else {
      document.body.classList.remove('chat-sidebar-open');
    }
    return () => {
      document.body.classList.remove('chat-sidebar-open');
    };
  }, [chatOpen, isDashboard]);

  const paddingStyle = isRtl
    ? { paddingLeft: chatOpen ? '380px' : '0px', transition: 'padding-left 0.3s ease-in-out' }
    : { paddingRight: chatOpen ? '380px' : '0px', transition: 'padding-right 0.3s ease-in-out' };

  const sidebarStyle = isRtl
    ? {
        position: 'fixed',
        top: 0,
        left: chatOpen ? '0' : '-380px',
        height: '100vh',
        width: '380px',
        transition: 'left 0.3s ease-in-out',
        zIndex: 1000,
        overflow: 'hidden'
      }
    : {
        position: 'fixed',
        top: 0,
        right: chatOpen ? '0' : '-380px',
        height: '100vh',
        width: '380px',
        transition: 'right 0.3s ease-in-out',
        zIndex: 1000,
        overflow: 'hidden'
      };

  return (
    <div 
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'var(--color-background)',
        color: 'var(--color-text)',
        fontFamily: "'Cairo', sans-serif",
        transition: 'background-color 0.3s ease, color 0.3s ease, padding-top 0.3s ease, padding-left 0.3s ease, padding-right 0.3s ease',
        paddingTop: `${devBarOffset}px`,
        ...(!isDashboard ? paddingStyle : {})
      }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      {/* Navbar */}
      {!isDashboard && (
        <Navbar
          session={session}
          theme={theme}
          setTheme={setTheme}
          onLogout={onLogout}
        />
      )}
      
      {/* Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>

      {/* Footer & Consent */}
      {!isDashboard && <Footer />}
      {!isDashboard && <CookieConsentBanner />}

      {/* Fixed Chat sidebar */}
      {!isDashboard && (
        <div
          style={sidebarStyle}
          className={`bg-[var(--color-surface)] border-[var(--card-border)] ${isRtl ? 'border-r' : 'border-l'} shadow-2xl`}
        >
          <Suspense fallback={null}>
            <LiveChatWidget session={session} isOpen={chatOpen} setIsOpen={setChatOpen} />
          </Suspense>
        </div>
      )}

      {!isDashboard && !chatOpen && (
        <button
          id="chat-toggle-button"
          onClick={() => setChatOpen(true)}
          aria-label="Open support live chat"
          className={`fixed bottom-6 ${isRtl ? 'left-6' : 'right-6'} z-50 flex items-center justify-center w-14 h-14 bg-gradient-to-tr from-violet-600 to-indigo-650 text-white rounded-full shadow-lg hover:scale-105 transition-transform duration-300`}
        >
          <MessageSquare className="w-6 h-6 animate-pulse" />
        </button>
      )}
    </div>
  );
}
